import { RadioGroup } from "@headlessui/react";
import { ReactElement } from "react";
import { FCWithClassName } from "types";

import { Mastercard, Postfinance, Twint, Visa } from "@/assets/icons/icons";
import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { DrCreditCardCardType } from "@/services/model";

/** Bare provider icons, sized by the consumer - not laid out for any one picker. */
export const paymentProviderIcons: Record<DrCreditCardCardType, ReactElement> =
  {
    TWI: <Twint />,
    ECA: <Mastercard />,
    VIS: <Visa />,
    PFC: <Postfinance />,
  };

export const paymentProviderLabels: Record<DrCreditCardCardType, string> = {
  TWI: "TWINT",
  ECA: "Mastercard",
  VIS: "Visa",
  PFC: "PostFinance",
};

export const paymentProvidersObject = {
  TWI: {
    label: paymentProviderLabels.TWI,
    value: DrCreditCardCardType.TWI,
    icon: paymentProviderIcons.TWI,
  },
  ECA: {
    label: paymentProviderLabels.ECA,
    value: DrCreditCardCardType.ECA,
    icon: paymentProviderIcons.ECA,
  },
  VIS: {
    label: paymentProviderLabels.VIS,
    value: DrCreditCardCardType.VIS,
    icon: paymentProviderIcons.VIS,
  },
  PFC: {
    label: paymentProviderLabels.PFC,
    value: DrCreditCardCardType.PFC,
    icon: paymentProviderIcons.PFC,
  },
};

export const paymentProviders: PaymentOption[] = Object.values(
  paymentProvidersObject,
);

interface PaymentOption {
  label: string;
  subtext?: string;
  value: string;
  icon?: ReactElement;
  disabled?: boolean;
}

interface Props {
  paymentOptions?: PaymentOption[];
  selected?: string | undefined;
  onChange?: (s: DrCreditCardCardType) => void;
  /**
   * `"inline"` (default): icon sits directly between the radio and the label,
   * per the `dire-radio` Figma spec.
   * `"trailing"`: icon pushed to the far right of the row - legacy layout.
   */
  iconPlacement?: "trailing" | "inline";
}

/**
 * Displays a choice of payment providers with radio buttons.
 */
export const PaymentProviderPicker: FCWithClassName<Props> = ({
  paymentOptions = paymentProviders,
  selected,
  onChange,
  className,
  iconPlacement = "inline",
}) => (
  <RadioGroup
    id="payment-provider-picker"
    className={className}
    value={selected || ""}
    onChange={(s: DrCreditCardCardType) => s && onChange?.(s)}
  >
    {paymentOptions.map(({ label, value, icon, disabled, subtext }) => (
      <div className="mb-6" key={value} data-testid={`payment-option-${value}`}>
        {iconPlacement === "inline" ? (
          <RadioButtonOption
            disabled={disabled}
            value={value}
            name="paymentOption"
            label={label}
            icon={icon}
          />
        ) : (
          <div className="flex items-center justify-between">
            <RadioButtonOption
              disabled={disabled}
              value={value}
              name="paymentOption"
              label={label}
            />
            <div>{icon}</div>
          </div>
        )}
        {subtext && (
          <div className="font-x-small ml-7 mt-2 text-foreground-primary">
            {subtext}
          </div>
        )}
      </div>
    ))}
  </RadioGroup>
);
